import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { OrganizationsService } from 'src/app/modules/organizations/services/organizations.service';
import { MdrDashboardsService } from '../../services/mdr-dashboards.service';
import { ApexChart, ApexDataLabels, ApexLegend, ApexNonAxisChartSeries, ApexPlotOptions, ApexResponsive, ApexStroke } from 'ng-apexcharts';
import { TimeframeService } from '../../../timeframe/services/timeframe.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { isEqual } from 'lodash-es';
import { TimeframeData } from '../../../timeframe/interfaces/TimeframeData';
import { OverviewWidgets } from '../../interfaces/mdr-dashboards-widgets.interface';
import { OrganizationData } from '../../../organizations/interfaces/OrganizationData';
import { Subscription } from 'rxjs';
import { IdentityService } from '../../../auth/services/identity.service';

export type ChartOptions = {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    responsive: ApexResponsive[];
    dataLabels: ApexDataLabels;
    labels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    legend: ApexLegend;
    stroke: ApexStroke;
};

@Component({
    selector: 'app-mdr-dashboards-overview',
    templateUrl: './mdr-dashboards-overview.component.html',
    styleUrls: ['./mdr-dashboards-overview.component.scss'],
    standalone: false,
})
export class MdrDashboardsOverviewComponent implements OnInit, OnDestroy {
    public dataSource: OverviewWidgets;
    public organization: OrganizationData;
    public orgSubscription: Subscription;
    public timeframeSubscription: Subscription;
    public currentTab: string;

    public constructor(
        public identityService: IdentityService,
        public cdr: ChangeDetectorRef,
        public timeframeService: TimeframeService,
        public organizationService: OrganizationsService,
        public mdrDashboardsService: MdrDashboardsService,
    ) {}

    ngOnDestroy(): void {
        this.timeframeSubscription?.unsubscribe();
        this.orgSubscription?.unsubscribe();
    }

    public ngOnInit() {
        this.timeframeSubscription = this.timeframeService
            .selectedTimeframe$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((timeframe: TimeframeData) => {
                this.updateDataSource(timeframe, this.organizationService.currentlySelectedOrganization());
                this.cdr.detectChanges();
                this.mdrDashboardsService.performDetectChanges.next(true);
            });

        this.orgSubscription = this.organizationService
            .selectedOrganization$()
            .pipe(distinctUntilChanged(isEqual))
            .subscribe((organization: OrganizationData) => {
                this.organization = organization;
                this.updateDataSource(this.timeframeService.currentlySelectedTimeframe(), organization);
                this.cdr.detectChanges();
                this.mdrDashboardsService.performDetectChanges.next(true);
            });

        this.mdrDashboardsService.currentTabHasChangedObs().subscribe((tabChanged: number) => {
            this.currentTab = 'tab-' + tabChanged;
        });
    }

    /**
     * Use this function to update each widget when one of the selectors (organization and timeframe) are changed
     * @param timeframe - retrieve data for the selected timeframe
     * @param organization - retrieve data for the selected organization
     */
    public updateDataSource(timeframe: TimeframeData | null, organization: OrganizationData | null) {
        const companyId = organization?.id || this.identityService.company?.id;
        let activitySummaryEndpoint = this.mdrDashboardsService.isLcoCompany(this.organization)
            ? '/mdr-foundations-activity-summary'
            : '/activity-summary';
        let isPremiumComp = this.mdrDashboardsService.isPremiumEnterprise(organization);
        this.dataSource = {
            xdr_alerts: {
                title: isPremiumComp ? 'XDR licenses:' : 'Licenses:',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/xdr-license', organization),
            },
            indicatorOptions: [
                {
                    title: 'Total EDR licenses',
                    value: 100,
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/total-licenses', organization),
                    delta: '',
                    restrict: this.mdrDashboardsService.isLcoCompany(this.organization),
                    id: 'total_edr_licenses_indicator',
                },
                {
                    title: 'Monitored endpoints',
                    value: 202,
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/monitored-endpoints', organization),
                    delta: '',
                    id: 'monitored_endpoints_indicator',
                },
                {
                    title: 'Active incidents',
                    value: 78,
                    data: this.mdrDashboardsService.getWidgetData(
                        timeframe?.name,
                        '/active-incidents',
                        organization,
                        false,
                        undefined,
                        false,
                    ),
                    delta: '',
                    showCheckMarkOnEmpty: true,
                    titleForEmpty: 'No active incident',
                    id: 'active_incidents_indicator',
                },
                {
                    title: 'Open verification requests',
                    value: 16,
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/open-verification-requests', organization),
                    delta: '',
                    drilldown: {
                        show: true,
                        drilldownTo: '/customer-verification-requests/browse',
                        drilldownFilters: [
                            { item: 'timeframe' },
                            {
                                item: {
                                    apiName: 'status',
                                    nativeName: 'status',
                                    customValue: ['0', '1'],
                                    target: 'status',
                                },
                            },
                        ],
                    },
                },
                {
                    title: 'Open recommendations',
                    value: 16,
                    data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/open-recommendations', organization),
                    delta: '',
                    showCheckMarkOnEmpty: true,
                    titleForEmpty: 'No open recommendation',
                    drilldown: {
                        show: true,
                        drilldownTo: '/recommendations/browse',
                        drilldownFilters: [
                            { item: 'timeframe' },
                            {
                                item: {
                                    apiName: 'description',
                                    nativeName: 'type',
                                    customValue: '0',
                                },
                            },
                        ],
                    },
                    id: 'open_recommendations_indicator',
                },
            ],
            activitySummary: {
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, activitySummaryEndpoint, organization),
                //data: of(activitySummary),
                colors: ['#67B7DC'],
                title: 'Activity summary',
                widgets: [
                    {
                        url: '/active-incidents',
                        title: 'Incidents',
                        isMainWidget: true,
                        icon: 'incidents.svg',
                        hasChart: false,
                        hasPill: false,
                        data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/active-incidents', organization),
                        checkMarkForEmptyValue: true,
                        showForEmptyValue: true,
                        chooseFromData: true,
                        chooseNameFromData: 'incidents',
                        strokeColors: [],
                        id: 'activity_summary_incidents',
                    },
                    {
                        title: 'Alerts triaged',
                        isMainWidget: false,
                        icon: 'alerts_triaged.svg',
                        hasChart: true,
                        hasPill: true,
                        data: this.mdrDashboardsService.getWidgetData(
                            timeframe?.name,
                            '/alerts-triaged',
                            organization,
                            false,
                            '/v2/reporting',
                            false,
                        ),
                        checkMarkForEmptyValue: false,
                        showForEmptyValue: false,
                        chooseFromData: true,
                        chooseNameFromData: 'Alerts triaged',
                        chartColor: '#295CA5',
                        strokeColors: ['#295CA5'],
                        id: 'activity_summary_alerts_triaged',
                    },
                    {
                        title: 'Investigations',
                        isMainWidget: false,
                        icon: 'investigations.svg',
                        hasChart: true,
                        hasPill: true,
                        data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/activity-trend', organization, false, '', false),
                        checkMarkForEmptyValue: false,
                        showForEmptyValue: false,
                        chooseFromData: true,
                        chooseNameFromData: 'Investigations',
                        chartColor: '#3AA1CC',
                        strokeColors: ['#3AA1CC'],
                        id: 'activity_summary_investigations',
                    },
                    {
                        title: 'Threat hunts',
                        isMainWidget: false,
                        icon: 'Hunts.svg',
                        hasChart: true,
                        hasPill: true,
                        data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/activity-trend', organization, false, '', false),
                        checkMarkForEmptyValue: false,
                        showForEmptyValue: false,
                        chooseFromData: true,
                        chooseNameFromData: 'Threat hunts',
                        chartColor: '#A367DC',
                        strokeColors: ['#A367DC'],
                        id: 'activity_summary_hunts',
                    },
                    {
                        title: 'Response actions',
                        isMainWidget: false,
                        icon: 'response_action.svg',
                        hasChart: false,
                        hasPill: true,
                        data: this.mdrDashboardsService.getWidgetData(
                            timeframe?.name,
                            '/total-response-actions',
                            organization,
                            false,
                            '/v2/reporting',
                            false,
                        ),
                        checkMarkForEmptyValue: false,
                        showForEmptyValue: false,
                        chooseFromData: false,
                        chooseNameFromData: '',
                        strokeColors: [],
                        id: 'activity_summary_response_actions',
                    },
                    {
                        title: 'Recommendations',
                        isMainWidget: false,
                        icon: 'recommendations.svg',
                        hasChart: false,
                        hasPill: true,
                        data: this.mdrDashboardsService.getWidgetData(
                            timeframe?.name,
                            '/total-recommendations',
                            organization,
                            false,
                            '/v2/reporting',
                            false,
                        ),
                        checkMarkForEmptyValue: false,
                        showForEmptyValue: false,
                        chooseFromData: false,
                        chooseNameFromData: '',
                        strokeColors: [],
                        id: 'activity_summary_recommendations',
                    },
                    {
                        title: 'Service status',
                        isMainWidget: true,
                        icon: '',
                        hasChart: false,
                        hasPill: true,
                        data: this.mdrDashboardsService.getWidgetData(
                            timeframe?.name,
                            '/service-status',
                            organization,
                            true,
                            '/v2/companies/' + companyId,
                            false,
                        ),
                        checkMarkForEmptyValue: true,
                        showForEmptyValue: true,
                        chooseFromData: false,
                        chooseNameFromData: '',
                        strokeColors: [],
                        id: 'activity_summary_service_status',
                    },
                ],
            },
            topInvestigationCategories: {
                id: 'top_investigation_categories_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-investigation-categories', organization),
                no_rows: 10,
                columns: ['category', 'value'],
                columnsWidth: [
                    {
                        column: 'category',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                title: 'Top investigation categories',
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: '/investigations/browse',
                    drilldownTitle: true,
                    drilldownFilters: [{ item: 'timeframe' }, { item: 'category' }],
                    drilldownTitleFilters: [{ item: 'timeframe' }],
                },
            },
            top10ImpactedUsers: {
                id: 'top_10_impacted_users_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-impacted-users', organization),
                no_rows: 10,
                columns: ['name', 'value'],
                columnsWidth: [
                    {
                        column: 'name',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                title: 'Top impacted users',
            },
            topEndpointsByAlerts: {
                id: 'top_endpoints_by_alerts_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/top-endpoints-alerts', organization),
                no_rows: 10,
                columns: ['logo', 'name', 'value'],
                columnsWidth: [
                    {
                        column: 'name',
                        width: '80%',
                    },
                    {
                        column: 'value',
                        width: '20%',
                    },
                ],
                title: 'Top endpoints by alerts',
            },
            recentActivity: {
                id: 'recent_activity_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/recent-activity', organization),
                no_rows: 5,
                columns: ['date', 'category', 'description'],
                columnsWidth: [
                    {
                        column: 'date',
                        width: '25%',
                    },
                    {
                        column: 'category',
                        width: '20%',
                    },
                    {
                        column: 'description',
                        width: '55%',
                    },
                ],
                title: 'Recent activity',
                drilldown: {
                    show: true,
                    hover_color: 'secondary',
                    drilldownTo: [
                        {
                            category: 'Investigation',
                            url: '/investigations/browse',
                        },
                        {
                            category: 'Recommendation',
                            url: '/recommendations/browse',
                        },
                        {
                            category: 'Incident',
                            url: '/reports/browse',
                        },
                        {
                            category: 'Threat Hunt',
                            url: '/threat-hunts/browse',
                        },
                    ],
                    drilldownFilters: [
                        { item: 'id' },
                        { item: 'cid' },
                        {
                            item: {
                                apiName: 'cname',
                                nativeName: 'company_name',
                            },
                        },
                    ],
                    drilldownTitle: false,
                    drilldownTitleFilters: [],
                },
            },
            recentDocuments: {
                id: 'recent_documents_table',
                data: this.mdrDashboardsService.getWidgetData(timeframe?.name, '/recent-documents', organization),
                no_rows: 5,
                columns: ['id', 'date', 'category', 'details'],
                columnsWidth: [
                    {
                        column: 'date',
                        width: '33%',
                    },
                    {
                        column: 'category',
                        width: '33%',
                    },
                    {
                        column: 'details',
                        width: '33%',
                    },
                ],
                title: 'Recent documents',
                drilldown: {
                    show: !this.mdrDashboardsService.isLcoCompany(this.organization),
                    drilldownTo: [
                        {
                            category: 'Report',
                            url: '/reports/browse',
                        },
                        {
                            category: 'Document',
                            url: '/documents/browse',
                        },
                    ],
                    drilldownFilters: [
                        { item: 'id' },
                        { item: 'cid' },
                        {
                            item: {
                                apiName: 'cname',
                                nativeName: 'company_name',
                            },
                        },
                    ],
                    drilldownTitle: false,
                    drilldownTitleFilters: [],
                },
            },
        };
    }

    public getIndicatorWidgets() {
        return this.dataSource.indicatorOptions.filter((widget) => !widget.restrict);
    }
}
